import React from "react";
import {
  Compass,
  Pencil,
  Code2,
  Rocket,
  LifeBuoy,
} from "lucide-react";

const steps = [
  {
    id: "01",
    icon: Compass,
    title: "Discover",
    desc:
      "We sit with you for a week. Audit the problem, the audience, and what success actually looks like — beyond the brief.",
    deliverables: ["Audit doc", "User journeys", "Tech feasibility"],
  },
  {
    id: "02",
    icon: Pencil,
    title: "Design",
    desc:
      "Prototypes you can press. Material 3 system tailored to your brand, motion specs included.",
    deliverables: ["Wireframes", "Design system", "Hi-fi prototype"],
  },
  {
    id: "03",
    icon: Code2,
    title: "Develop",
    desc:
      "Kotlin + Compose. Modular. Tested. Reviewed every Friday on builds you can install yourself.",
    deliverables: ["Weekly builds", "Tests", "CI/CD"],
  },
  {
    id: "04",
    icon: Rocket,
    title: "Deploy",
    desc:
      "Store listing, staged rollout, monitoring on day zero. We do the boring scary parts.",
    deliverables: ["Play store launch", "Analytics", "Crash monitoring"],
  },
  {
    id: "05",
    icon: LifeBuoy,
    title: "Support",
    desc:
      "We don't ghost. Retainers, OS upgrades, A/B tests — we stay around if you want us to.",
    deliverables: ["SLAs", "Roadmap reviews", "A/B testing"],
  },
];

const Process = () => {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative bg-ink py-24 text-cream md:py-36"
    >
      <div className="pointer-events-none absolute inset-0 bg-stripes opacity-40" />

      <div className="relative mx-auto max-w-[1480px] px-6 md:px-12">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <div className="font-mono text-xs uppercase tracking-[0.22em] text-acid">
                04 — How we work
              </div>

              <h2 className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.92] tracking-tightest md:text-7xl">
                A process
                <br />
                that <span className="italic text-acid">earns trust.</span>
              </h2>

              <p className="mt-6 max-w-md font-body text-lg text-cream/70">
                Five chapters. No surprises. Every step has a deliverable you
                can hold and a meeting you can challenge us in.
              </p>

              <div className="mt-10 inline-flex items-center gap-3 rounded-sm border border-cream/30 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-cream/80">
                <span className="h-1.5 w-1.5 rounded-full bg-acid" />
                avg. timeline: 10–16 weeks
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ol className="relative space-y-10 md:space-y-12">
              {steps.map((s, i) => {
                const Icon = s.icon;

                return (
                  <li
                    key={s.id}
                    data-testid={`process-step-${i}`}
                    className="group relative grid grid-cols-12 gap-6 border-t border-cream/15 pt-10"
                  >
                    <div className="col-span-2 md:col-span-2">
                      <div className="font-mono text-sm text-acid">
                        {s.id}
                      </div>
                    </div>

                    <div className="col-span-10 md:col-span-10">
                      <div className="flex items-center gap-3">
                        <Icon
                          className="h-6 w-6 text-acid"
                          strokeWidth={1.6}
                        />

                        <h3 className="font-display text-4xl font-extrabold tracking-tightest md:text-5xl">
                          {s.title}
                        </h3>
                      </div>

                      <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-cream/70 md:text-lg">
                        {s.desc}
                      </p>

                      <div className="mt-5 flex flex-wrap gap-2">
                        {s.deliverables.map((d) => (
                          <span
                            key={d}
                            className="rounded-sm border border-cream/25 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/80"
                          >
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;